import React from 'react';
import { ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import {
  parseWordImageSize,
  WORD_IMAGE_SIZES,
  type WordImageSize,
} from './wordPageLayout';

interface WordImageSizeToggleProps {
  value: WordImageSize | string | null | undefined;
  onChange: (size: WordImageSize) => void;
  disabled?: boolean;
  /** Kompakte Overlay-Leiste auf dem Bild. */
  compact?: boolean;
  testId?: string;
}

const WordImageSizeToggle: React.FC<WordImageSizeToggleProps> = ({
  value,
  onChange,
  disabled,
  compact,
  testId = 'word-image-size',
}) => {
  const current = parseWordImageSize(value);
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={current}
      onChange={(_, next: WordImageSize | null) => {
        if (next) onChange(next);
      }}
      disabled={disabled}
      data-testid={testId}
      sx={{
        flexShrink: 0,
        ...(compact
          ? {
              bgcolor: '#1b2233',
              border: '1px solid rgba(255,255,255,0.22)',
              borderRadius: '8px',
            }
          : {}),
        '& .MuiToggleButton-root': {
          px: compact ? 0.75 : 1,
          py: compact ? 0.15 : 0.25,
          fontSize: compact ? '0.65rem' : '0.75rem',
          fontWeight: 700,
          lineHeight: 1.4,
          textTransform: 'none',
          ...(compact
            ? {
                color: '#f2f5fb',
                borderColor: 'rgba(255,255,255,0.16)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                '&.Mui-selected': {
                  bgcolor: '#3d7eff',
                  color: '#fff',
                  '&:hover': { bgcolor: '#5a90ff' },
                },
              }
            : {}),
        },
      }}
    >
      {WORD_IMAGE_SIZES.map((preset) => (
        <ToggleButton
          key={preset.id}
          value={preset.id}
          data-testid={`${testId}-${preset.id}`}
          aria-label={preset.label}
        >
          <Tooltip title={`${preset.label} · ${preset.hint}`} arrow>
            <Typography
              component="span"
              variant="caption"
              sx={{ fontWeight: 700, fontSize: 'inherit', color: 'inherit' }}
            >
              {compact ? preset.shortLabel : preset.label}
            </Typography>
          </Tooltip>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

export default WordImageSizeToggle;
